import type { CSSProperties } from 'react';

/** Paleta del WMS. Verde corporativo sobre gris frío, con ámbar y rojo sólo para avisos. */
export const c = {
  verde: '#007a47',
  verdeOscuro: '#005c35',
  verdeSuave: '#e6fff5',
  verdeBorde: '#b6e0cd',
  verdeTinte: '#f7fdfa',
  fondo: '#f5f7fa',
  panel: '#ffffff',
  texto: '#1a1a1a',
  texto2: '#575e68',
  // Los dos grises más claros tienen que leerse sobre el blanco del panel y
  // sobre `fondo` (#f5f7fa) con al menos 4,5:1 (WCAG AA), porque se miran
  // parados, con el celular en la mano y luz de nave. `texto4` era #9198a2
  // (2,9:1 sobre blanco) y `texto3` era #6b7280, justo 4,5:1 sobre `fondo`:
  // no quedaba lugar para un `texto4` legible y más claro, así que se oscureció
  // un poco `texto3` también.
  texto3: '#626974', // 5,54:1 sobre panel · 5,16:1 sobre fondo
  texto4: '#6a717c', // 4,92:1 sobre panel · 4,59:1 sobre fondo
  borde: '#e0e3e7',
  bordeFuerte: '#d1d5db',
  bordeTenue: '#c9ced6',
  ambar: '#996b00',
  ambarSuave: '#fdf4e3',
  rojo: '#c62828',
  rojoSuave: '#fdeaea',
  rojoFila: '#fdf6f6',
} as const;

/**
 * Con qué se pinta cada sector. El backend define el vocabulario y le da uno
 * distinto a cada sector al crearlo; acá se le pone el tono.
 */
export const COLOR_SECTOR: Record<string, string> = {
  verde: '#007a47',
  ambar: '#996b00',
  azul: '#2f6f9f',
  violeta: '#8a4fa0',
  ladrillo: '#a0522d',
  oliva: '#456a3f',
  turquesa: '#0f766e',
  rosa: '#a33d63',
};

const EN_ORDEN = Object.values(COLOR_SECTOR);

/**
 * El color de un sector. El índice es el respaldo para los que todavía no
 * tienen color propio guardado, y para que dos sin color no salgan iguales.
 */
export const colorSector = (color: string | null | undefined, indice = 0) =>
  (color && COLOR_SECTOR[color]) || EN_ORDEN[indice % EN_ORDEN.length]!;

/**
 * Con qué se pinta cada tipo de camino. El backend define el vocabulario
 * (circulación, peatonal…) y acá se le pone color, que es cosa de la vista.
 */
export const COLOR_CAMINO: Record<string, { relleno: string; borde: string }> = {
  circulacion: { relleno: '#e3e7ec', borde: '#c3cad3' },
  peatonal: { relleno: '#fdf0d5', borde: '#e6c98a' },
  montacargas: { relleno: '#e2edf6', borde: '#a9c6dd' },
  preparacion: { relleno: '#e3f6ec', borde: '#a8d8bf' },
};

export const colorCamino = (tipo: string) => COLOR_CAMINO[tipo] ?? COLOR_CAMINO.circulacion!;

/**
 * Con qué se pinta cada tipo de abertura (`TIPOS_ABERTURA` en
 * `backend/src/domain/planta.ts`). Vive acá, y no repetida en cada vista que
 * dibuja aberturas, porque tres copias («Racks.tsx», «Isometrico.tsx» y
 * «PlanoEditable.tsx») habían terminado con paletas distintas: un portón salía
 * naranja en el plano y en el 3D, y azul en el editor. Una sola tabla, un solo
 * color por tipo en toda la app.
 */
export const COLOR_ABERTURA: Record<string, string> = {
  porton: '#c07a1f',
  peatonal: '#3f6fb0',
  ventanal: '#5fa8c7',
  paso: '#4f9463',
};

export const colorAbertura = (tipo: string) => COLOR_ABERTURA[tipo] ?? c.texto3;

/**
 * El gris con el que se dibuja un tramo de muro. Vive acá por el mismo motivo
 * que `COLOR_ABERTURA`: lo dibujan el plano y el 3D, y dos copias del mismo
 * gris son dos copias que pueden divergir.
 */
export const COLOR_MURO = { relleno: '#aeb6c0', borde: '#7c8590' };

/** Escala de llenado de una posición: 0, 1, 2 o 3 niveles ocupados. */
export const LLENADO = ['#ffffff', '#eafaf2', '#c9efdd', '#007a47'] as const;

/**
 * Tonos de la escena 3D (etapa 3): chapa, bloque, durlock, hormigón, madera y
 * los grises/naranja del rack. Viven acá por el mismo motivo que
 * `COLOR_ABERTURA` y `COLOR_MURO`: un solo lugar, para que las tareas 5 y 6
 * (naves, racks) no inventen hexadecimales sueltos en el componente. **Las
 * tareas 5 y 6 consumen estos tonos y no definen colores propios** — si les
 * falta un matiz, se agrega acá, no en el componente.
 */
export const COLOR_3D = {
  /** Chapa acanalada gris clara, la de los muros perimetrales del seed. */
  chapa: '#b8bdc4',
  chapaSombra: '#8a919b',
  /** Bloque de hormigón blanco roto, con juntas más oscuras. */
  bloque: '#e8e4da',
  bloqueJunta: '#c9c4b6',
  /** Placa de durlock: yeso pintado liso, más claro y más "muerto" que el bloque. */
  durlock: '#e9e9e6',
  durlockJunta: '#d3d3ce',
  /** Losa de hormigón del piso. */
  hormigon: '#9a9d9f',
  hormigonRuido: '#8c8f91',
  /**
   * Madera de las tarimas de los huecos (`cajasDePallet`). Es un pino claro y
   * **poco saturado a propósito**: la tarima comparte pantalla con la carga,
   * que va pintada del color del sector, y los ocho tonos de `COLOR_SECTOR`
   * son medios y saturados. Con el marrón anaranjado de antes (`#b8905f`) el
   * sector **ámbar** —`#996b00`, el sector B del seed— quedaba del mismo tono
   * que la madera y el rack entero se leía como una mancha marrón: no se
   * distinguía el hueco cargado del vacío, que es justo el dato. Se ve en
   * `pallets-cerca.png`. Cualquier tono nuevo acá tiene que quedar más claro
   * que los de `COLOR_SECTOR`, no al lado.
   */
  madera: '#c9ad86',
  maderaVeta: '#a3855f',
  /** Estructura del rack: parante gris y larguero naranja, como en las fotos. */
  parante: '#6b7280',
  larguero: '#d97a1f',
} as const;

export const mono = "'Geist Mono', ui-monospace, monospace";

// ---------------------------------------------------------------- primitivas

export const tarjeta: CSSProperties = {
  background: c.panel,
  border: `1px solid ${c.borde}`,
  borderRadius: 16,
};

// Nada de interfaz por debajo de 12 px: se lee a un brazo de distancia.
export const rotulo: CSSProperties = {
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '.09em',
  textTransform: 'uppercase',
  color: c.texto3,
};

export const titulo: CSSProperties = {
  margin: 0,
  fontSize: 27,
  fontWeight: 600,
  letterSpacing: '-0.03em',
  color: c.texto,
};

export const subtitulo: CSSProperties = { marginTop: 6, fontSize: 14, color: c.texto2 };

export const cifra: CSSProperties = {
  fontSize: 34,
  fontWeight: 600,
  letterSpacing: '-0.03em',
  color: c.texto,
  fontVariantNumeric: 'tabular-nums',
};

export const pildora: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '10px 18px',
  borderRadius: 999,
  fontSize: 13.5,
  fontWeight: 600,
  cursor: 'pointer',
  // 44 px en pantalla táctil (`--toque` vive en global.css bajo
  // `pointer: coarse`); en escritorio no pide nada.
  minHeight: 'var(--toque, auto)',
};

/**
 * Un `minHeight` de diseño que en pantalla táctil no baja de 44 px. Hace falta
 * porque un `minHeight` puesto en el estilo del elemento le gana a la regla de
 * global.css: un botón con `minHeight: 36` quedaba en 36 aunque el celular
 * pidiera 44. En escritorio respeta el valor que se le pasa.
 */
export const altoMinimo = (px: number) => `max(var(--toque, 0px), ${px}px)`;

export const botonPrimario: CSSProperties = {
  ...pildora,
  border: `1px solid ${c.verde}`,
  background: c.verde,
  color: '#fff',
};

export const botonSecundario: CSSProperties = {
  ...pildora,
  border: `1px solid ${c.bordeFuerte}`,
  background: c.panel,
  color: c.texto,
};

/** Un botón que se ve como link: el código que abre su ficha. */
/** Botón redondo de un ícono, de 44 px para el dedo; `activo` lo pinta de verde. */
export const botonRedondo = (activo = false): CSSProperties => ({
  width: 44,
  height: 44,
  flex: '0 0 44px',
  borderRadius: 999,
  border: `1px solid ${activo ? c.verde : c.borde}`,
  background: activo ? c.verdeSuave : c.panel,
  color: activo ? c.verde : c.texto2,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 0,
  cursor: 'pointer',
});

export const enlace: CSSProperties = {
  border: 'none',
  background: 'none',
  padding: 0,
  font: 'inherit',
  fontFamily: mono,
  textAlign: 'left',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
  color: c.verde,
  cursor: 'pointer',
};

export const campo: CSSProperties = {
  height: 42,
  // En táctil llega a 44, como los botones (ver `pildora`).
  minHeight: 'var(--toque, auto)',
  padding: '0 14px',
  border: `1px solid ${c.bordeFuerte}`,
  borderRadius: 999,
  background: c.panel,
  fontSize: 13.5,
  color: c.texto,
  width: '100%',
  minWidth: 0,
};

export const etiquetaCampo: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  minWidth: 0,
  fontSize: 13,
  fontWeight: 500,
  color: c.texto,
};

/**
 * Una fila de campos de formulario que se acomoda sola.
 *
 * Con `grid` hay que elegir de antemano cuántas columnas entran, y una fila de
 * cinco campos con anchos mínimos suma más que un celular de 390 px: los campos
 * quedan aplastados y lo último se va afuera de la tarjeta. Con `flex` envuelto
 * cada campo pide un ancho cómodo y baja al renglón siguiente cuando no entra,
 * en cualquier ancho y sin un corte fijo que haya que adivinar.
 *
 * Los campos van con `anchoDeCampo`, que es lo que le dice a cada uno cuánto
 * espacio pedir.
 */
export const filaDeCampos: CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 12,
  alignItems: 'flex-end',
};

/**
 * Cuánto pide un campo dentro de `filaDeCampos`: crece y se achica, y arranca
 * pidiendo `base` píxeles. El `minWidth: 0` es lo que deja que un `select` con
 * una opción larga se achique en vez de estirar la fila.
 */
export const anchoDeCampo = (base: number): CSSProperties => ({ flex: `1 1 ${base}px`, minWidth: 0 });

export const encabezadoTabla: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '10px 16px',
  background: c.fondo,
  borderBottom: `1px solid ${c.borde}`,
  fontSize: 12,
  fontWeight: 600,
  letterSpacing: '.08em',
  textTransform: 'uppercase',
  color: c.texto3,
};

export const filaTabla: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  padding: '12px 16px',
  borderBottom: `1px solid ${c.borde}`,
  fontSize: 13.5,
  color: c.texto,
};

export const nota: CSSProperties = {
  padding: '12px 14px',
  border: `1px dashed ${c.bordeFuerte}`,
  borderRadius: 16,
  fontSize: 12.5,
  color: c.texto2,
};

// -------------------------------------------------------------------- tonos

export type Tono = 'ok' | 'warn' | 'err' | 'neu';

const TONOS: Record<Tono, { fg: string; bg: string; bd: string; dot: string }> = {
  ok: { fg: c.verde, bg: c.verdeSuave, bd: c.verde, dot: c.verde },
  warn: { fg: c.ambar, bg: c.ambarSuave, bd: c.ambar, dot: c.ambar },
  err: { fg: c.rojo, bg: c.rojoSuave, bd: c.rojo, dot: c.rojo },
  neu: { fg: c.texto2, bg: c.fondo, bd: c.borde, dot: c.texto4 },
};

export const tono = (t: Tono) => TONOS[t];

/** Un tono que viene como texto de la base; lo desconocido, neutro. */
export const tonoDeTexto = (t: string): Tono => (t === 'ok' || t === 'warn' || t === 'err' ? t : 'neu');

/** Traduce un estado de negocio al tono visual que le corresponde. */
export function tonoDeEstado(estado: string): Tono {
  switch (estado) {
    case 'Disponible':
    case 'Confirmado':
    case 'Descargado completo':
    case 'Finalizado':
    case 'Despachada':
    // El capacho entregado ya salió del depósito: es el final feliz del
    // circuito, igual que una OT despachada.
    case 'Entregado':
      return 'ok';
    case 'En fraccionamiento':
    case 'En descarga':
    case 'En armado':
    // Aceptado es "alguien lo tomó y todavía está acá": hay algo que hacer.
    case 'Aceptado':
      return 'warn';
    case 'Procesado con diferencias':
      return 'err';
    default:
      return 'neu';
  }
}

export const chip = (t: Tono): CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 7,
  padding: '4px 11px',
  borderRadius: 999,
  background: TONOS[t].bg,
  border: `1px solid ${TONOS[t].bd}`,
  fontSize: 12,
  fontWeight: 600,
  color: TONOS[t].fg,
  whiteSpace: 'nowrap',
});

export const punto = (t: Tono): CSSProperties => ({
  width: 7,
  height: 7,
  borderRadius: 999,
  background: TONOS[t].dot,
  flex: '0 0 7px',
});

/** Aviso a pantalla completa: verde para confirmaciones, rojo para errores. */
export const aviso = (t: Tono): CSSProperties => ({
  padding: '14px 18px',
  borderRadius: 16,
  background: TONOS[t].bg,
  border: `1px solid ${TONOS[t].bd}`,
  fontSize: 13.5,
  color: c.texto,
});

/** Botón de filtro / navegación que puede estar activo. */
export const chipBoton = (activo: boolean): CSSProperties => ({
  padding: '8px 14px',
  borderRadius: 999,
  border: `1px solid ${activo ? c.verde : c.borde}`,
  background: activo ? c.verdeSuave : c.panel,
  color: activo ? c.verde : c.texto2,
  fontSize: 13,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  cursor: 'pointer',
});

export const miles = (n: number) => n.toLocaleString('es-AR');

/**
 * El huso del depósito. Toda fecha y hora se muestra en hora de Argentina,
 * no en la del equipo: una PC o un celular con otro huso mostraba las horas
 * corridas.
 */
export const HUSO = 'America/Argentina/Buenos_Aires';

/**
 * Hoy en `YYYY-MM-DD`, en Argentina. No `toISOString()`, que está en UTC: de
 * 21 a 24 daba la fecha de mañana.
 */
export const hoyISO = () => new Date().toLocaleDateString('sv-SE', { timeZone: HUSO });

/** «11/09/2026» a partir de una fecha ISO con hora (la que devuelve la API). */
export const fechaCorta = (iso: string) =>
  new Date(iso).toLocaleDateString('es-AR', { timeZone: HUSO, day: '2-digit', month: '2-digit', year: 'numeric' });

/** «11/09/2026» a partir del `YYYY-MM-DD` del campo de fecha. */
export function fechaImpresa(iso: string): string {
  const [a, m, d] = iso.split('-');
  return a && m && d ? `${d}/${m}/${a}` : iso;
}
